export type ActionTone = 'success' | 'info' | 'danger' | 'neutral';

/** Tokens whose casing a naive capitalise would get wrong. */
const ACRONYMS: Record<string, string> = {
  api: 'API',
  smtp: 'SMTP',
  ip: 'IP',
  id: 'ID',
  llm: 'LLM',
  otp: 'OTP',
  kyc: 'KYC',
  pd: 'PD',
};

const humanise = (snake: string): string =>
  snake
    .split('_')
    .filter(Boolean)
    .map((w) => ACRONYMS[w] ?? w)
    .join(' ');

const sentenceCase = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** `role.permissions_updated` → "Role permissions updated". */
export function actionLabel(action: string): string {
  if (!action) return '—';
  const [resource, ...rest] = action.split('.');
  const verb = rest.join(' ');
  const words = verb ? `${humanise(resource)} ${humanise(verb)}` : humanise(resource);
  return sentenceCase(words);
}

/** Colour intent for the verb chip; unknown verbs stay neutral so the table never guesses. */
export function actionVerbTone(action: string): ActionTone {
  const verb = action.split('.').pop() ?? '';
  if (/deleted|removed|revoked/.test(verb)) return 'danger';
  if (/created|restored|generated|published|activated/.test(verb)) return 'success';
  if (/updated|toggled|changed|rotated|synced|reset|applied|cloned/.test(verb)) return 'info';
  return 'neutral';
}

export function resourceLabel(type: string | null, id: string | null): string {
  if (!type) return '—';
  const base = sentenceCase(humanise(type));
  return id ? `${base} #${id}` : base;
}

/** Chip classes per tone — same palette the Audit Trail status chips use, so the two pages read as one. */
export const TONE_CHIP_CLASS: Record<ActionTone, string> = {
  success: 'text-emerald-700 bg-emerald-100 dark:text-emerald-300 dark:bg-emerald-950/50',
  info: 'text-sky-800 bg-sky-100 dark:text-sky-200 dark:bg-sky-950/50',
  danger: 'text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-950/50',
  neutral: 'text-gray-500 bg-gray-100 dark:text-gray-400 dark:bg-white/5',
};

export function outcomeChipClass(outcome: string): string {
  return outcome === 'failure' ? TONE_CHIP_CLASS.danger : TONE_CHIP_CLASS.success;
}
